import { useEffect, useMemo, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  Calendar, DollarSign, Clock, ArrowRight, TrendingUp, TrendingDown,
  AlertCircle, Search, GraduationCap, BookOpen, Repeat, Info,
} from 'lucide-react';
import BackOrHomeButton from '@/components/BackOrHomeButton';
import SiteDisclaimer from '@/components/SiteDisclaimer';
import { useSeoMeta } from '@/pages/learn/LearnPage';
import { useSeoStockModal } from '@/components/SeoStockModal';
import FixedRateAssetCard, { isFixedRateAsset } from '@/components/FixedRateAssetCard';
import SplitNote from '@/components/SplitNote';
import { DeclaredPill, EstimateDisclaimer } from '@/components/DeclaredPill';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * "[TICKER] ex-dividend date" — programmatic SEO landing (Feb 2026).
 *
 * Route: /ticker/:ticker/ex-dividend-date
 *
 * Sibling of /is/:ticker/a-dividend-stock — targets commercial-intent
 * queries like "AAPL ex dividend date", "SCHD next ex dividend date",
 * "when is JEPI's ex-div date". Data comes from
 * /api/stocks/:ticker/ex-dividend-info, which reads directly from
 * stock_cache (populated by Massive API — no yfinance).
 *
 * Page structure:
 *   H1 → "When is [TICKER]'s next ex-dividend date?"
 *   Hero card → next ex-div date + countdown days + payment date/amount
 *   Ex-div timeline → last 8 payments as a visual list
 *   Recent change strip → increase / decrease / flat with delta %
 *   Educational block → what an ex-div date means (compact)
 *   Peer alternatives → tickers with upcoming ex-div dates
 *   CTA row → Screener / Dashboard / Learn
 *   FAQ + JSON-LD schema
 */
export default function ExDividendDate() {
  const { openStock, modal: stockModal } = useSeoStockModal();
  const { ticker: tickerParam } = useParams();
  const ticker = (tickerParam || '').toUpperCase().trim();
  const tickerLower = ticker.toLowerCase();
  const navigate = useNavigate();

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!ticker) {
      navigate('/', { replace: true });
      return;
    }
    let cancelled = false;
    setLoading(true);
    setError(null);
    axios.get(`${API}/stocks/${ticker}/ex-dividend-info`)
      .then((r) => { if (!cancelled) { setData(r.data); setLoading(false); } })
      .catch((e) => {
        if (cancelled) return;
        setError(e?.response?.status === 404 ? 'not-tracked' : 'fetch-failed');
        setLoading(false);
      });
    return () => { cancelled = true; };
  }, [ticker, navigate]);

  // Countdown days until next ex-div — recomputed on data change.
  const countdownDays = useMemo(() => {
    if (!data?.next_ex_dividend_date) return null;
    const parsed = parseDate(data.next_ex_dividend_date);
    if (!parsed) return null;
    const now = new Date();
    now.setHours(0, 0, 0, 0);
    const diff = Math.round((parsed.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
    return diff;
  }, [data?.next_ex_dividend_date]);

  const seoTitle = data
    ? (data.verdict === 'has_ex_div' && data.next_ex_dividend_date
        ? `${ticker} Ex-Dividend Date — Next: ${data.next_ex_dividend_date} | DiviDrip`
        : data.verdict === 'has_ex_div'
          ? `${ticker} Ex-Dividend Date — Latest ${data.ex_dividend_date || 'schedule'} | DiviDrip`
          : data.verdict === 'money_market_fund'
            ? `${ticker} Ex-Dividend Date — Money Market Fund | DiviDrip`
            : data.verdict === 'mutual_fund'
              ? `${ticker} Ex-Dividend Date — Mutual Fund, Not Tracked | DiviDrip`
              : `${ticker} Ex-Dividend Date — Not Applicable | DiviDrip`)
    : `${ticker} Ex-Dividend Date | DiviDrip`;

  const seoDescription = data
    ? (data.verdict === 'has_ex_div'
        ? `${ticker}'s next ex-dividend date${data.next_ex_dividend_date ? ` is ${data.next_ex_dividend_date}` : ''}. Next payment${data.next_payment_date ? ` on ${data.next_payment_date}` : ''} — $${fmtNum(data.next_payment_amount || data.last_payment_amount)} per share. See full ex-div history.`
        : data.verdict === 'money_market_fund'
          ? `${ticker} is a money market fund. It pays monthly income distributions instead of scheduled dividends. 7-day yield ${fmtPct(data.mmf_7day_yield)}.`
          : data.verdict === 'mutual_fund'
            ? `${ticker} is an open-end mutual fund. Mutual funds set distribution record dates rather than exchange ex-dividend dates, and DiviDrip does not track mutual fund distributions — check the fund family for its schedule.`
            : `${ticker} does not have an ex-dividend date because it does not pay a dividend. See dividend-paying alternatives in ${data.sector || 'the same sector'}.`)
    : `Find ${ticker}'s next ex-dividend date, payment date, and dividend history — powered by DiviDrip's live tracking of 5,350+ tickers.`;

  useSeoMeta({
    title: seoTitle,
    description: seoDescription,
    canonical: `https://twylightcrow.com/ticker/${tickerLower}/ex-dividend-date`,
  });

  // JSON-LD FAQ + Event schema. FAQPage carries user questions; the
  // Event schema pins the actual ex-dividend date so Google can pull
  // it into rich results.
  useEffect(() => {
    if (!data) return;
    const scripts = [];

    // FAQ schema
    const faqs = buildFaqs(ticker, data, countdownDays);
    const faqLd = {
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: faqs.map((f) => ({
        '@type': 'Question',
        name: f.q,
        acceptedAnswer: { '@type': 'Answer', text: f.a },
      })),
    };
    const s1 = document.createElement('script');
    s1.type = 'application/ld+json';
    s1.text = JSON.stringify(faqLd);
    s1.setAttribute('data-testid', 'ex-div-jsonld-faq');
    document.head.appendChild(s1);
    scripts.push(s1);

    // Event schema for the upcoming ex-div (only if we have a date).
    if (data.verdict === 'has_ex_div' && data.next_ex_dividend_date) {
      const _d = parseDate(data.next_ex_dividend_date);
      const iso = _d ? `${_d.getFullYear()}-${String(_d.getMonth() + 1).padStart(2, '0')}-${String(_d.getDate()).padStart(2, '0')}` : null;
      if (iso) {
        const eventLd = {
          '@context': 'https://schema.org',
          '@type': 'Event',
          name: `${ticker} Ex-Dividend Date`,
          startDate: iso,
          eventStatus: 'https://schema.org/EventScheduled',
          eventAttendanceMode: 'https://schema.org/OnlineEventAttendanceMode',
          location: {
            '@type': 'VirtualLocation',
            url: `https://twylightcrow.com/ticker/${tickerLower}/ex-dividend-date`,
          },
          organizer: { '@type': 'Organization', name: 'DiviDrip', url: 'https://twylightcrow.com/' },
          description: `${ticker} goes ex-dividend on ${data.next_ex_dividend_date}. Shareholders on record before this date receive the upcoming ${data.next_payment_amount ? `$${fmtNum(data.next_payment_amount)} per share ` : ''}dividend${data.next_payment_date ? ` on ${data.next_payment_date}` : ''}.`,
        };
        const s2 = document.createElement('script');
        s2.type = 'application/ld+json';
        s2.text = JSON.stringify(eventLd);
        s2.setAttribute('data-testid', 'ex-div-jsonld-event');
        document.head.appendChild(s2);
        scripts.push(s2);
      }
    }

    return () => { scripts.forEach((s) => s.remove()); };
  }, [ticker, tickerLower, data, countdownDays]);

  if (loading) {
    return (
      <div className="max-w-4xl mx-auto px-4 py-12" data-testid="ex-div-loading">
        <div className="animate-pulse space-y-4">
          <div className="h-8 w-72 bg-slate-200 dark:bg-slate-800 rounded" />
          <div className="h-32 bg-slate-200 dark:bg-slate-800 rounded" />
          <div className="h-64 bg-slate-200 dark:bg-slate-800 rounded" />
        </div>
      </div>
    );
  }

  if (error === 'not-tracked') {
    return (
      <div className="max-w-3xl mx-auto px-4 py-16" data-testid="ex-div-notfound">
        <BackOrHomeButton />
        <h1 className="text-3xl font-bold mt-6 mb-3 text-slate-900 dark:text-slate-100">
          {ticker} isn&apos;t in our database yet.
        </h1>
        <p className="text-slate-600 dark:text-slate-400 mb-8">
          We track 5,350+ dividend-paying tickers, but {ticker} isn&apos;t one of them yet. Try our screener to find similar tickers.
        </p>
        <div className="flex flex-wrap gap-3">
          <Link to="/screener" className="px-5 py-2.5 bg-cyan-600 hover:bg-cyan-500 text-white font-semibold rounded-lg" data-testid="ex-div-notfound-screener">Open Screener</Link>
          <Link to="/" className="px-5 py-2.5 border border-slate-300 dark:border-slate-700 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-lg" data-testid="ex-div-notfound-home">Popular dividend stocks</Link>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-3xl mx-auto px-4 py-16" data-testid="ex-div-error">
        <BackOrHomeButton />
        <h1 className="text-2xl font-bold mt-6 mb-3">Something went wrong loading {ticker}.</h1>
        <p className="text-slate-600 dark:text-slate-400">Please try again in a moment.</p>
      </div>
    );
  }

  const isDivPayer = data.verdict === 'has_ex_div';
  // Oct 2026 — the actual payment pair behind the raise/cut strip (only when it
  // agrees in direction with the cached change, so we never print a cut's dates
  // under a "Raised" headline).
  const chgDetail = data.recent_change && data.recent_change_pct != null
    && Math.sign(data.recent_change.pct) === Math.sign(data.recent_change_pct) ? data.recent_change : null;
  const isMmf = data.verdict === 'money_market_fund' || data.verdict === 'mutual_fund';
  const isNonDiv = data.verdict === 'non_dividend' || data.verdict === 'structural_non_dividend';

  return (
    <>
    <div className="max-w-4xl mx-auto px-4 pt-8 sm:pt-12 pb-0" data-testid="ex-dividend-date-page">
      <BackOrHomeButton />

      {/* Breadcrumb */}
      <nav aria-label="Breadcrumb" className="mt-4 text-xs text-slate-500 dark:text-slate-400" data-testid="ex-div-breadcrumb">
        <Link to="/" className="hover:text-cyan-600 dark:hover:text-cyan-400">Home</Link>
        <span className="mx-2 opacity-50">/</span>
        <Link to="/screener" className="hover:text-cyan-600 dark:hover:text-cyan-400">Screener</Link>
        <span className="mx-2 opacity-50">/</span>
        <span className="text-slate-700 dark:text-slate-300">{ticker} ex-dividend date</span>
      </nav>

      <h1 className="mt-4 text-3xl sm:text-4xl font-black text-slate-900 dark:text-slate-100 leading-tight" data-testid="ex-div-h1">
        {isDivPayer
          ? `When is ${ticker}'s next ex-dividend date?`
          : isMmf
            ? `${ticker} ex-dividend date`
            : `${ticker} ex-dividend date`}
      </h1>
      {data.name && data.name !== ticker && (
        <p className="mt-1 text-sm text-slate-500 dark:text-slate-400" data-testid="ex-div-company-name">
          {data.name}
          {data.sector ? <span className="opacity-60"> · {data.sector}</span> : null}
        </p>
      )}

      {/* Hero card — next ex-div date with countdown, or verdict for non-payers */}
      {isDivPayer ? (
        <div className="mt-6 rounded-xl border-2 border-cyan-300 dark:border-cyan-700/50 bg-cyan-50 dark:bg-cyan-950/30 overflow-hidden" data-testid="ex-div-hero-card">
          <div className="p-6 sm:p-7 flex items-start gap-4">
            <Calendar className="w-10 h-10 text-cyan-600 dark:text-cyan-400 shrink-0" strokeWidth={2.2} />
            <div className="min-w-0 flex-1">
              <div className="text-xs uppercase tracking-wider font-bold text-cyan-700 dark:text-cyan-300 inline-flex items-center gap-2">
                Next ex-dividend date
                <DeclaredPill declared={data.next_schedule ? data.next_schedule.declared : null} testId="ex-div-next-status" />
              </div>
              <div className="text-3xl sm:text-4xl font-black text-slate-900 dark:text-slate-100 mt-1" data-testid="ex-div-next-date">
                {data.next_ex_dividend_date || 'Pending'}
              </div>
              {countdownDays != null && data.next_ex_dividend_date && (
                <div className="mt-2 text-sm" data-testid="ex-div-countdown">
                  {countdownDays > 0
                    ? <span className="text-slate-700 dark:text-slate-300"><strong className="text-cyan-700 dark:text-cyan-300">{countdownDays}</strong> {countdownDays === 1 ? 'day' : 'days'} away — buy shares before this date to receive the dividend</span>
                    : countdownDays === 0
                      ? <span className="text-amber-700 dark:text-amber-300 font-semibold">Ex-dividend today — trade must settle before market open</span>
                      : <span className="text-slate-600 dark:text-slate-400">Passed {Math.abs(countdownDays)} {Math.abs(countdownDays) === 1 ? 'day' : 'days'} ago</span>}
                </div>
              )}
            </div>
          </div>
          {/* Payment strip inside the hero */}
          <div className="grid grid-cols-2 sm:grid-cols-4 divide-x divide-cyan-200 dark:divide-cyan-900/60 border-t border-cyan-200 dark:border-cyan-900/60 bg-white/40 dark:bg-slate-950/30">
            <HeroStat label={data.next_schedule && !data.next_schedule.declared ? 'Payment date (est.)' : 'Payment date'} value={data.next_payment_date} testId="ex-div-stat-paydate" />
            <HeroStat label={data.next_schedule && !data.next_schedule.declared ? 'Payment amount (est.)' : 'Payment amount'} value={data.next_payment_amount || data.last_payment_amount ? `$${fmtNum(data.next_payment_amount || data.last_payment_amount)}` : '—'} testId="ex-div-stat-payamount" />
            <HeroStat label="Frequency" value={data.frequency || (data.payments_per_year ? `${data.payments_per_year}×/yr` : '—')} testId="ex-div-stat-frequency" />
            <HeroStat label="Yield" value={fmtPct(data.dividend_yield)} testId="ex-div-stat-yield" />
          </div>
          {data.next_schedule && !data.next_schedule.declared && (
            <div className="border-t border-cyan-200 dark:border-cyan-900/60 bg-amber-50/70 dark:bg-amber-950/20 px-4 py-2.5">
              <EstimateDisclaimer next={data.next_schedule} testId="ex-div-estimate-disclaimer" />
            </div>
          )}
        </div>
      ) : (
        <div className={`mt-6 p-6 rounded-xl border-2 flex items-start gap-4 ${
          isMmf
            ? 'bg-cyan-50 dark:bg-cyan-950/30 border-cyan-300 dark:border-cyan-700/50'
            : 'bg-slate-50 dark:bg-slate-900/60 border-slate-200 dark:border-slate-800'
        }`} data-testid="ex-div-verdict-nonpayer">
          {data.verdict === 'mutual_fund'
            ? <Info className="w-8 h-8 shrink-0 text-cyan-600 dark:text-cyan-400" />
            : <AlertCircle className={`w-8 h-8 shrink-0 ${isMmf ? 'text-cyan-600 dark:text-cyan-400' : 'text-slate-500'}`} />}
          <div>
            <div className="text-lg sm:text-xl font-bold text-slate-900 dark:text-slate-100">{data.headline}</div>
            {isMmf && data.mmf_7day_yield != null && (
              <p className="mt-2 text-sm text-slate-700 dark:text-slate-300">
                Money market funds like {ticker} pay monthly interest distributions instead of scheduled dividends. Current 7-day SEC yield: <strong>{fmtPct(data.mmf_7day_yield)}</strong>.
              </p>
            )}
            {data.verdict === 'mutual_fund' && (              <p className="mt-2 text-sm text-slate-700 dark:text-slate-300">
                Mutual funds set a distribution record date and pay at the next
                once-a-day NAV &mdash; there is no exchange ex-dividend date to
                track. DiviDrip does not carry mutual fund distribution
                schedules, so we make no claim about {ticker}&apos;s dates.{' '}
                <Link to="/learn/mutual-fund-tickers" className="underline">How
                single-X mutual funds work</Link>.
              </p>
            )}
            <div className="mt-3 flex flex-wrap gap-x-4 gap-y-2 text-sm">
              <Link to={`/is/${tickerLower}/a-dividend-stock`} className="text-cyan-600 dark:text-cyan-400 hover:underline inline-flex items-center gap-1" data-testid="ex-div-nonpayer-check">
                Is {ticker} a dividend stock? <ArrowRight className="w-3.5 h-3.5" />
              </Link>
              <Link to="/screener" className="text-cyan-600 dark:text-cyan-400 hover:underline inline-flex items-center gap-1" data-testid="ex-div-nonpayer-screener">
                Find dividend payers <ArrowRight className="w-3.5 h-3.5" />
              </Link>
            </div>
          </div>
        </div>
      )}

      {/* Recent change strip (dividend payers only) */}
      {isDivPayer && data.recent_change_pct != null && (
        <div
          className={`mt-4 inline-flex items-center gap-2 px-4 py-2 rounded-lg border text-sm ${
            data.recent_change_pct > 0
              ? 'bg-emerald-50 dark:bg-emerald-950/30 border-emerald-200 dark:border-emerald-800/50 text-emerald-800 dark:text-emerald-300'
              : data.recent_change_pct < 0
                ? 'bg-rose-50 dark:bg-rose-950/30 border-rose-200 dark:border-rose-800/50 text-rose-800 dark:text-rose-300'
                : 'bg-slate-50 dark:bg-slate-900 border-slate-200 dark:border-slate-800 text-slate-700 dark:text-slate-300'
          }`}
          data-testid="ex-div-recent-change"
        >
          {data.recent_change_pct > 0
            ? <TrendingUp className="w-4 h-4" />
            : data.recent_change_pct < 0
              ? <TrendingDown className="w-4 h-4" />
              : <Repeat className="w-4 h-4" />}
          <span>
            {data.recent_change_pct > 0 ? 'Raised' : data.recent_change_pct < 0 ? 'Cut' : 'Held flat'}
            {' '}by <strong>{data.recent_change_pct > 0 ? '+' : ''}{fmtPct(Math.abs(chgDetail ? chgDetail.pct : data.recent_change_pct)).replace('%', '')}%</strong>
            {chgDetail ? (
              <span data-testid="ex-div-recent-change-detail">
                {' — from '}<strong>${fmtNum(chgDetail.from_amount)}</strong>
                <span className="opacity-80"> (ex-div {formatIsoDate(chgDetail.from_date)})</span>
                {' to '}<strong>${fmtNum(chgDetail.to_amount)}</strong>
                <span className="opacity-80"> (ex-div {formatIsoDate(chgDetail.to_date)})</span>
              </span>
            ) : data.recent_change_prev_amount ? <> — from ${fmtNum(data.recent_change_prev_amount)} to ${fmtNum(data.last_payment_amount || data.next_payment_amount)}</> : null}
          </span>
        </div>
      )}

      {/* Growth streak badge */}
      {isDivPayer && data.years_of_growth ? (
        <div className="mt-3 inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800/50 text-sm text-amber-800 dark:text-amber-300" data-testid="ex-div-growth-streak">
          <TrendingUp className="w-4 h-4" />
          <span>
            <strong>{data.years_of_growth}-year</strong> dividend growth streak
            {data.tier ? <> · <strong>{titleCase(data.tier)}</strong></> : null}
          </span>
        </div>
      ) : null}

      {/* Oct 2026 — Asset Class card for preferreds / baby bonds (same card as the Stock Modal). */}
      {isDivPayer && isFixedRateAsset(data) && (
        <div className="mt-6" data-testid="ex-div-fixed-rate-card">
          <FixedRateAssetCard stock={data} />
        </div>
      )}

      {/* Ex-div timeline — last 8 payments */}
      {isDivPayer && Array.isArray(data.ex_div_timeline) && data.ex_div_timeline.length > 0 && (
        <div className="mt-8" data-testid="ex-div-timeline">
          <h2 className="text-xl font-bold text-slate-900 dark:text-slate-100 mb-3">
            {ticker}&apos;s recent ex-dividend dates
          </h2>
          <SplitNote
            splits={data.splits}
            since={data.ex_div_timeline[data.ex_div_timeline.length - 1]?.date}
            suffix="— amounts in this table are split-adjusted"
            testId="ex-div-split-note"
            className="mb-3"
          />
          <div className="rounded-xl border border-slate-200 dark:border-slate-800 overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 dark:bg-slate-900/70 text-left text-[11px] uppercase tracking-wider text-slate-500 dark:text-slate-400">
                <tr>
                  <th className="px-4 py-2 font-semibold">Ex-dividend date</th>
                  <th className="px-4 py-2 font-semibold text-right">Amount per share</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-200 dark:divide-slate-800">
                {data.ex_div_timeline.map((row, i) => (
                  <tr
                    key={i}
                    className={`${i % 2 === 0 ? 'bg-emerald-100/80 dark:bg-emerald-950/25' : 'bg-sky-100/80 dark:bg-sky-950/25'} hover:brightness-95 dark:hover:brightness-125 transition-[filter]`}
                    data-testid={`ex-div-timeline-row-${i}`}
                  >
                    <td className="px-4 py-2.5 text-slate-800 dark:text-slate-200 font-mono">{formatIsoDate(row.date)}</td>
                    <td className="px-4 py-2.5 text-right font-mono font-semibold text-emerald-600 dark:text-emerald-400">${fmtNum(row.amount)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Educational block — what is an ex-dividend date */}
      <div className="mt-8 p-5 rounded-xl bg-slate-50 dark:bg-slate-900/60 border border-slate-200 dark:border-slate-800" data-testid="ex-div-education">
        <h2 className="text-lg font-bold text-slate-900 dark:text-slate-100 flex items-center gap-2">
          <Clock className="w-5 h-5 text-slate-500" /> What&apos;s an ex-dividend date, exactly?
        </h2>
        <p className="mt-2 text-sm text-slate-700 dark:text-slate-300 leading-relaxed">
          The <strong>ex-dividend date</strong> is the first trading day on which a stock trades <em>without</em> its next dividend attached. Buy the stock the day BEFORE the ex-div date and you get the upcoming payment. Buy ON or AFTER, and the seller keeps it. The share price typically opens ~1 dividend lower on the ex-date to reflect the payment leaving the company.
        </p>
        <Link to="/learn/ex-dividend-date-timeline" className="inline-flex items-center gap-1 mt-3 text-sm text-cyan-600 dark:text-cyan-400 hover:underline" data-testid="ex-div-timeline-guide">
          <BookOpen className="w-3.5 h-3.5" /> Full ex-dividend timeline guide
        </Link>
      </div>

      {/* Peer alternatives — same-sector dividend payers with upcoming ex-div */}
      {Array.isArray(data.peer_alternatives) && data.peer_alternatives.length > 0 && (
        <div className="mt-8" data-testid="ex-div-peers">
          <h2 className="text-xl font-bold text-slate-900 dark:text-slate-100 mb-3">
            {isDivPayer
              ? `Other dividend stocks with upcoming ex-dividend dates`
              : `Dividend-paying alternatives in the same sector`}
          </h2>
          <div className="grid sm:grid-cols-2 gap-3">
            {data.peer_alternatives.map((peer, idx) => {
              const peerTicker = (peer.ticker || '').toUpperCase();
              if (!peerTicker) return null;
              return (
                <Link
                  key={peerTicker}
                  to={`/ticker/${peerTicker.toLowerCase()}/ex-dividend-date`}
                  className="block p-4 rounded-lg border border-slate-200 dark:border-slate-800 hover:border-cyan-400 dark:hover:border-cyan-600 hover:bg-slate-50 dark:hover:bg-slate-900/60 transition-colors group"
                  data-testid={`ex-div-peer-${idx}`}
                >
                  <div className="flex items-center justify-between gap-3">
                    <div className="min-w-0">
                      <div className="font-bold text-slate-900 dark:text-slate-100 font-mono">{peerTicker}</div>
                      <div className="text-xs text-slate-500 dark:text-slate-400 truncate">{peer.name || peer.sector || ''}</div>
                      {peer.next_ex_date ? (
                        <div className="text-[11px] mt-1 text-cyan-700 dark:text-cyan-300 font-mono">Next ex-div: {peer.next_ex_date}</div>
                      ) : null}
                    </div>
                    <div className="text-right shrink-0">
                      {peer.dividend_yield != null && (
                        <div className="text-sm font-bold text-emerald-600 dark:text-emerald-400">{fmtPct(peer.dividend_yield)}</div>
                      )}
                      <ArrowRight className="w-4 h-4 text-slate-400 group-hover:text-cyan-500 mt-1 ml-auto transition-transform group-hover:translate-x-0.5" />
                    </div>
                  </div>
                </Link>
              );
            })}
          </div>
        </div>
      )}

      {/* CTA row into the rest of the app */}
      <div className="mt-10 p-6 rounded-xl bg-gradient-to-br from-cyan-50 to-emerald-50 dark:from-slate-900 dark:to-slate-900/60 border border-slate-200 dark:border-slate-800" data-testid="ex-div-cta-row">
        <h2 className="text-lg font-bold text-slate-900 dark:text-slate-100 mb-3">Track {ticker} in your portfolio</h2>
        <div className="flex flex-wrap gap-3">
          <button type="button" onClick={() => openStock(ticker, 'dividend-info')} className="inline-flex items-center gap-2 px-4 py-2 bg-slate-900 hover:bg-slate-800 dark:bg-slate-100 dark:hover:bg-white dark:text-slate-900 text-white font-semibold rounded-lg" data-testid="ex-div-cta-open-modal">
            <DollarSign className="w-4 h-4" /> Open full dividend history
          </button>
          <Link to="/screener" className="inline-flex items-center gap-2 px-4 py-2 bg-cyan-600 hover:bg-cyan-500 text-white font-semibold rounded-lg" data-testid="ex-div-cta-screener">
            <Search className="w-4 h-4" /> Open Screener
          </Link>
          <Link to={`/is/${tickerLower}/a-dividend-stock`} className="inline-flex items-center gap-2 px-4 py-2 border border-slate-300 dark:border-slate-700 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-lg" data-testid="ex-div-cta-check">
            Is {ticker} a dividend stock?
          </Link>
          <Link to="/learn" className="inline-flex items-center gap-2 px-4 py-2 border border-slate-300 dark:border-slate-700 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-lg" data-testid="ex-div-cta-learn">
            <GraduationCap className="w-4 h-4" /> Learn dividends
          </Link>
        </div>
      </div>

      <FaqSection ticker={ticker} data={data} countdownDays={countdownDays} />
      </div>
      <SiteDisclaimer />
      {stockModal}
    </>
  );
}

/* ─── Sub-components ────────────────────────────────────────────── */

function HeroStat({ label, value, testId }) {
  return (
    <div className="px-4 py-3" data-testid={testId}>
      <div className="text-[10px] uppercase tracking-wider font-semibold text-slate-500 dark:text-slate-400">{label}</div>
      <div className="mt-0.5 text-sm font-bold text-slate-900 dark:text-slate-100 font-mono">{value || '—'}</div>
    </div>
  );
}

function FaqSection({ ticker, data, countdownDays }) {
  const faqs = buildFaqs(ticker, data, countdownDays);
  return (
    <div className="mt-10" data-testid="ex-div-faq">
      <h2 className="text-xl font-bold text-slate-900 dark:text-slate-100 mb-3">Frequently asked</h2>
      <div className="space-y-3">
        {faqs.map((f, i) => (
          <details key={i} className="p-4 rounded-lg border border-slate-200 dark:border-slate-800 group" data-testid={`ex-div-faq-item-${i}`}>
            <summary className="cursor-pointer font-semibold text-slate-900 dark:text-slate-100 list-none flex items-start justify-between gap-3">
              <span>{f.q}</span>
              <ArrowRight className="w-4 h-4 mt-1 shrink-0 text-slate-400 group-open:rotate-90 transition-transform" />
            </summary>
            <p className="mt-3 text-sm text-slate-700 dark:text-slate-300 leading-relaxed">{f.a}</p>
          </details>
        ))}
      </div>
    </div>
  );
}

/* ─── Content helpers ───────────────────────────────────────────── */

function buildFaqs(ticker, data, countdownDays) {
  const faqs = [];
  const name = data.name || ticker;

  if (data.verdict === 'has_ex_div') {
    faqs.push({
      q: `When is ${ticker}'s next ex-dividend date?`,
      a: data.next_ex_dividend_date
        ? `${ticker}'s next ex-dividend date is ${data.next_ex_dividend_date}${countdownDays != null && countdownDays > 0 ? ` (${countdownDays} days away)` : ''}. You must own shares before this date to qualify for the upcoming dividend.`
        : `${ticker}'s next ex-dividend date has not yet been announced. The last ex-div date was ${data.ex_dividend_date || 'unknown'}.`,
    });
    faqs.push({
      q: `How much will ${ticker}'s next dividend be?`,
      a: data.next_payment_amount
        ? `${ticker}'s next dividend payment is scheduled for $${fmtNum(data.next_payment_amount)} per share${data.next_payment_date ? ` on ${data.next_payment_date}` : ''}.`
        : data.last_payment_amount
          ? `${ticker}'s most recent dividend was $${fmtNum(data.last_payment_amount)} per share. The next payment amount hasn't been officially announced.`
          : `The next dividend amount for ${ticker} has not yet been announced.`,
    });
    faqs.push({
      q: `How often does ${ticker} pay a dividend?`,
      a: `${ticker} pays a dividend ${data.frequency ? data.frequency.toLowerCase() : (data.payments_per_year ? `${data.payments_per_year} times per year` : 'on a regular schedule')}${data.dividend_yield ? ` with a current yield of ${fmtPct(data.dividend_yield)}` : ''}.`,
    });
    faqs.push({
      q: `Should I buy ${ticker} before or after the ex-dividend date?`,
      a: `To receive the upcoming dividend, you must buy ${ticker} at least one full trading day BEFORE its ex-dividend date. Buying on or after the ex-date means the seller keeps the payout. That said, the stock typically opens the ex-date roughly one dividend lower, so the timing rarely changes total return — it just changes whether the money arrives as cash or is baked into the share price.`,
    });
  } else if (data.verdict === 'mutual_fund') {
    faqs.push({
      q: `Does ${ticker} have an ex-dividend date?`,
      a: `${ticker} is an open-end mutual fund, and mutual funds do not have exchange ex-dividend dates the way stocks and ETFs do — the fund sets a distribution record date and pays at the next once-a-day NAV. DiviDrip does not track mutual fund distribution schedules because no data source we read carries them, so we make no claim about ${ticker}'s dates. The fund family publishes its own distribution calendar.`,
    });
    faqs.push({
      q: `Which funds does DiviDrip track distributions for?`,
      a: `Money market funds — the five-letter double-XX tickers — are covered in full, including 7-day SEC yields and net assets sourced from SEC Form N-MFP filings. Ordinary five-letter single-X mutual funds are not.`,
    });
  } else if (data.verdict === 'money_market_fund') {
    faqs.push({
      q: `Does ${ticker} have an ex-dividend date?`,
      a: `${ticker} is a money market fund. It doesn't have a traditional ex-dividend date because MMFs pay monthly interest distributions accrued daily. Your position on any given day earns that day's income.`,
    });
    faqs.push({
      q: `How do money market funds pay income?`,
      a: `Money market funds like ${ticker} accrue interest daily and distribute it monthly. The current 7-day SEC yield of ${fmtPct(data.mmf_7day_yield)} is the annualized version of what you actually earned over the past week.`,
    });
  } else {
    faqs.push({
      q: `Does ${ticker} have an ex-dividend date?`,
      a: `No. ${ticker} (${name}) does not pay a dividend, so there is no ex-dividend date to track. Companies without a dividend program don't set record or payment dates.`,
    });
    faqs.push({
      q: `Will ${ticker} ever pay a dividend?`,
      a: `Companies typically initiate dividends as they mature. DiviDrip watches for new dividend initiations across our full 5,350+ ticker universe — if ${ticker} announces a dividend, this page will surface its first ex-dividend date within 24 hours.`,
    });
    faqs.push({
      q: `Which similar tickers pay dividends?`,
      a: data.peer_alternatives?.length
        ? `Consider ${data.peer_alternatives.slice(0, 3).map((p) => p.ticker).join(', ')} — dividend payers in the same or a related sector. Full comparison in the DiviDrip screener.`
        : `Use the DiviDrip Screener to filter dividend payers in ${data.sector || 'the same sector'} by yield, quality score, and payout ratio.`,
    });
  }

  faqs.push({
    q: `Where does DiviDrip's ex-dividend data come from?`,
    a: `DiviDrip pulls ex-dividend dates directly from Massive Market Data, which mirrors the exchange-declared record and pay dates for every US-listed dividend payer. Data refreshes automatically as new declarations are filed.`,
  });

  return faqs;
}

/* ─── Formatters ────────────────────────────────────────────────── */

function fmtPct(v) {
  if (v == null || isNaN(Number(v))) return '—';
  // API yields are already percent units (AAPL 0.33 = 0.33%) — never rescale.
  return `${Number(v).toFixed(2)}%`;
}
function fmtNum(v) {
  if (v == null || isNaN(Number(v))) return '—';
  // Match the stock modal: up to 4 decimals for per-share amounts ($0.2457),
  // trailing zeros trimmed but never fewer than 2 ($0.25, $1.10).
  const s = Number(v).toFixed(4).replace(/0+$/, '');
  const [whole, dec = ''] = s.split('.');
  return `${whole}.${dec.length > 2 ? dec.padEnd(4, '0') : dec.padEnd(2, '0')}`;
}
function parseDate(s) {
  if (!s) return null;
  // ISO "2026-10-01" is parsed by browsers as UTC MIDNIGHT, which renders as
  // the previous evening in US timezones (Oct 1 → "Sep 30"). Build it as a
  // LOCAL calendar date instead so the table matches the stock modal.
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(s));
  const d = m ? new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3])) : new Date(s);
  if (isNaN(d.getTime())) return null;
  return d;
}
function formatIsoDate(s) {
  const d = parseDate(s);
  if (!d) return String(s || '—');
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}
function titleCase(s) {
  if (!s) return '';
  return String(s).replace(/\b\w/g, (c) => c.toUpperCase());
}
